import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Chip,
  Card,
  CardContent,
  Alert,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  alpha,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Person,
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  Badge,
  CheckCircle,
  Cancel,
  LocalHospital,
  PersonAdd,
  AccountCircle,
} from '@mui/icons-material';
import { 
  getUsers, 
  createUser, 
  updateUser, 
  deleteUser,
  User, 
  UserCreate 
} from '../api/api';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserCreate>({
    username: '',
    password: '',
    name: '',
    email: '',
    cpf: '',
    rg: '',
    crm: '',
    user_type: 'user',
    address: '',
    phone: '',
    is_active: true
  });

  const userTypes = [
    { value: 'doctor', label: 'Doctor', icon: <LocalHospital /> },
    { value: 'patient', label: 'Patient', icon: <Person /> },
    { value: 'admin', label: 'Admin', icon: <Badge /> },
    { value: 'user', label: 'User', icon: <AccountCircle /> }
  ];

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers({ limit: 500, active_only: false });
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        password: '', // Don't pre-fill password for editing
        name: user.name,
        email: user.email || '',
        cpf: user.cpf || '',
        rg: user.rg || '',
        crm: user.crm || '',
        user_type: user.user_type,
        address: user.address || '',
        phone: user.phone || '',
        is_active: user.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        password: '',
        name: '',
        email: '',
        cpf: '',
        rg: '',
        crm: '',
        user_type: 'user',
        address: '',
        phone: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
  };

  const handleInputChange = (field: keyof UserCreate) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.username.trim()) {
      setError('Name and username are required');
      return;
    }

    if (!editingUser && !formData.password.trim()) {
      setError('Password is required for new users');
      return;
    }

    if (formData.user_type === 'doctor' && !formData.crm?.trim()) {
      setError('CRM is required for doctors');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        // For updates, exclude password if not provided
        const updateData: Partial<UserCreate> = { ...formData };
        if (!updateData.password) {
          const { password, ...dataWithoutPassword } = updateData;
          await updateUser(editingUser.id, dataWithoutPassword);
        } else {
          await updateUser(editingUser.id, updateData);
        }
        setSuccess('User updated successfully');
      } else {
        await createUser(formData);
        setSuccess('User created successfully');
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error: any) {
      if (error.response?.status === 400) {
        setError('Username or CPF already exists');
      } else {
        setError('Failed to save user');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user: User) => {
    if (window.confirm(`Are you sure you want to deactivate ${user.name}?`)) {
      try {
        await deleteUser(user.id);
        setSuccess('User deactivated successfully');
        fetchUsers();
      } catch (error) {
        setError('Failed to deactivate user');
      }
    }
  };

  const formatDate = (dateString: string | null) => {
    return dateString ? new Date(dateString).toLocaleDateString('pt-BR') : 'Never';
  };

  const getUserTypeIcon = (userType: string) => {
    const type = userTypes.find(t => t.value === userType);
    return type?.icon || <AccountCircle />;
  };

  const getUserTypeLabel = (userType: string) => {
    const type = userTypes.find(t => t.value === userType);
    return type?.label || userType;
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={40} sx={{ color: '#6366f1' }} />
        <Typography variant="body2" color="text.secondary">
          Loading users...
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              User Management
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              Manage system users, doctors, patients, and administrators
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Add User
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 4 }}>
          <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Person sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {users.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Users
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {users.filter(u => u.is_active).length} active
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <LocalHospital sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {users.filter(u => u.user_type === 'doctor').length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Doctors
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Badge sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {users.filter(u => u.user_type === 'admin').length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Administrators
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        {/* Users Table */}
        <Paper 
          sx={{ 
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#6366f1', 0.05) }}>
                  <TableCell sx={{ fontWeight: 700, color: '#374151', py: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person sx={{ fontSize: 16, color: '#6366f1' }} />
                      User
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone sx={{ fontSize: 16, color: '#6366f1' }} />
                      Contact
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Documents</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Last Login</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow 
                    key={user.id} 
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha('#6366f1', 0.02),
                        transition: 'all 0.2s ease'
                      },
                      borderBottom: index === users.length - 1 ? 'none' : '1px solid #e2e8f0'
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          @{user.username}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getUserTypeIcon(user.user_type)}
                        label={getUserTypeLabel(user.user_type)}
                        size="small"
                        sx={{
                          backgroundColor: alpha('#6366f1', 0.1),
                          color: '#6366f1',
                          fontWeight: 600,
                          borderRadius: 2
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        {user.phone && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <Phone sx={{ fontSize: 14, mr: 1, verticalAlign: 'middle' }} />
                            {user.phone}
                          </Typography>
                        )}
                        {user.email && (
                          <Typography variant="body2">
                            <Email sx={{ fontSize: 14, mr: 1, verticalAlign: 'middle' }} />
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {user.cpf && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            CPF: {user.cpf}
                          </Typography>
                        )}
                        {user.rg && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            RG: {user.rg}
                          </Typography>
                        )}
                        {user.crm && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            CRM: {user.crm}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.last_login)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={user.is_active ? <CheckCircle /> : <Cancel />}
                        label={user.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          backgroundColor: user.is_active 
                            ? alpha('#10b981', 0.1) 
                            : alpha('#ef4444', 0.1),
                          color: user.is_active ? '#10b981' : '#ef4444',
                          fontWeight: 600,
                          borderRadius: 2
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(user)}
                            sx={{ color: '#6366f1' }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {user.is_active && (
                          <Tooltip title="Deactivate">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(user)}
                              sx={{ color: '#ef4444' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {users.length === 0 && !loading && (
          <Box textAlign="center" py={8}>
            <Person sx={{ fontSize: 64, color: '#e2e8f0', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} color="text.primary" gutterBottom>
              No users found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
              Add your first user to start managing system access.
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Add First User
            </Button>
          </Box>
        )}

        {/* User Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingUser ? 'Edit User' : 'Add New User'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Full Name *"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Username *"
                  value={formData.username}
                  onChange={handleInputChange('username')}
                  variant="outlined"
                  disabled={!!editingUser}
                />
              </Stack>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label={editingUser ? "New Password (leave blank to keep current)" : "Password *"}
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange('password')}
                  variant="outlined"
                />
                <TextField
                  select
                  fullWidth
                  label="User Type *"
                  value={formData.user_type}
                  onChange={handleInputChange('user_type')}
                  variant="outlined"
                >
                  {userTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  variant="outlined"
                />
              </Stack>
              
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="CPF"
                  value={formData.cpf}
                  onChange={handleInputChange('cpf')}
                  variant="outlined"
                  placeholder="XXX.XXX.XXX-XX"
                />
                <TextField
                  fullWidth
                  label="RG"
                  value={formData.rg}
                  onChange={handleInputChange('rg')}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="CRM"
                  value={formData.crm}
                  onChange={handleInputChange('crm')}
                  variant="outlined"
                  disabled={formData.user_type !== 'doctor'}
                  helperText={formData.user_type === 'doctor' ? 'Required for doctors' : ''}
                />
              </Stack>
              
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={handleInputChange('address')}
                variant="outlined"
                multiline
                rows={2}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleInputChange('is_active')}
                  />
                }
                label="Active User"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              {saving ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Messages */}
        {success && (
          <Alert 
            severity="success" 
            onClose={() => setSuccess(null)}
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          >
            {error}
          </Alert>
        )}
      </Box>
    </Fade>
  );
};

export default UserManagement;